// PAM (SSH) policy model: pure helpers the adapter, its sections and the
// gateway's Policy tab share. No React, no services, so it is unit-tested.
//
// Stored shape (policyType "SSH"): policyJson.ssh = { users (emails), groups
// (directory group names), matchAll, hosts, accounts, jumpServers?, sudo? }.

export const SSH_POLICY_TYPE = "SSH";

// host or host:port (a non-standard sshd port). No user@, no spaces.
export const HOST_RE = /^[A-Za-z0-9._-]+(:\d{1,5})?$/;

export const cleanList = (v) => [...new Set((Array.isArray(v) ? v : []).map((x) => String(x ?? "").trim()).filter(Boolean))];

// The address an endpoint is named by everywhere on the SSH path: the gateway
// dials it, the policy names it, and its key is issued for it.
export const endpointAddress = (r) => r?.privateIp || r?.publicIp || r?.ipAddress || "";

export const endpointLabel = (r) => {
  const address = endpointAddress(r);
  return r?.hostName && r.hostName !== address ? `${r.hostName} (${address})` : address || r?.hostName || "";
};

// The onboarded endpoint a policy host names, the way the backend matches it
// when issuing keys: private IP, public IP or hostname, ignoring any port.
export const endpointForHost = (instances, host) => {
  const h = String(host || "").split(":")[0].toLowerCase();
  if (!h) return null;
  return (instances || []).find((r) => [r.privateIp, r.publicIp, r.hostName].some((v) => v && String(v).toLowerCase() === h)) || null;
};

// The gateways a policy applies through: those it names (older policies carry
// ssh.jumpServers, and the backend then filters on them), else the gateways of
// the endpoints its hosts name.
export const policyGatewayIds = (policyJson, instances) => {
  const ssh = policyJson?.ssh || {};
  const named = (ssh.jumpServers || []).map(Number).filter(Boolean);
  if (named.length) return [...new Set(named)];
  return [...new Set((ssh.hosts || []).map((h) => Number(endpointForHost(instances, h)?.jumpServerId)).filter(Boolean))];
};

/* ------------------------------------------------------------------ sudo */
// ssh.sudo elevates sessions this policy lets in when they were approved with
// MFA: full sudo, or named commands, for at most maxMinutes. These mirror the
// backend (sudogrants.NormalisePolicySudo / ValidCommand), in plain words.

export const SUDO_DEFAULT_MINUTES = 60;
export const SUDO_MIN_MINUTES = 5;
export const SUDO_MAX_MINUTES = 720;
export const SUDO_MAX_COMMANDS = 32;
const SUDO_MAX_COMMAND_LEN = 256;
const SUDO_COMMAND_CHARS = /^[A-Za-z0-9/._+@%" -]+$/;

export const SUDO_MODES = [
  { value: "none", label: "None" },
  { value: "full", label: "Full sudo" },
  { value: "commands", label: "Specific commands" },
];

export const SUDO_WORDS = {
  badCommand: (c) => `${c} isn't a full path. Start with / and use letters, digits, spaces and / . _ + @ % " - only.`,
  noCommands: "Add at least one command, or choose Full sudo.",
  tooMany: `Add at most ${SUDO_MAX_COMMANDS} commands.`,
  minutes: `Enter a time limit between ${SUDO_MIN_MINUTES} and ${SUDO_MAX_MINUTES} minutes.`,
  other: "Check the sudo settings.",
};

export const validSudoCommand = (c) =>
  c.length >= 2 && c.length <= SUDO_MAX_COMMAND_LEN && c[0] === "/" && SUDO_COMMAND_CHARS.test(c) && !c.includes("  ");

// One command per line: trimmed, blank lines dropped, duplicates removed.
export const parseSudoCommands = (text) => [...new Set(String(text || "").split("\n").map((l) => l.trim()).filter(Boolean))];

/** { "where.sudo"?, "where.sudoMinutes"? } for the draft's product. */
export function sudoErrors(p) {
  const e = {};
  if (!p || p.sudoMode === "none" || !p.sudoMode) return e;
  if (p.sudoMode === "commands") {
    const cmds = parseSudoCommands(p.sudoCommands);
    const bad = cmds.find((c) => !validSudoCommand(c));
    if (bad) e["where.sudo"] = SUDO_WORDS.badCommand(bad);
    else if (cmds.length > SUDO_MAX_COMMANDS) e["where.sudo"] = SUDO_WORDS.tooMany;
    else if (!cmds.length) e["where.sudo"] = SUDO_WORDS.noCommands;
  }
  const m = p.sudoMinutes;
  if (!Number.isInteger(m) || m < SUDO_MIN_MINUTES || m > SUDO_MAX_MINUTES) e["where.sudoMinutes"] = SUDO_WORDS.minutes;
  return e;
}

// ssh.sudo as the backend stores it, or undefined for none (the key is omitted).
export const buildSudo = (p) => {
  if (p.sudoMode === "full") return { full: true, maxMinutes: p.sudoMinutes };
  if (p.sudoMode === "commands") return { commands: parseSudoCommands(p.sudoCommands), maxMinutes: p.sudoMinutes };
  return undefined;
};

// A stored ssh.sudo back into the draft's three fields.
export const sudoFields = (sudo) => {
  const minutes = Number(sudo?.maxMinutes) || SUDO_DEFAULT_MINUTES;
  if (sudo?.full) return { sudoMode: "full", sudoCommands: "", sudoMinutes: minutes };
  if ((sudo?.commands || []).length) return { sudoMode: "commands", sudoCommands: sudo.commands.join("\n"), sudoMinutes: minutes };
  return { sudoMode: "none", sudoCommands: "", sudoMinutes: SUDO_DEFAULT_MINUTES };
};

export const sudoText = (sudo) => {
  if (!sudo) return "None";
  const mins = `${Number(sudo.maxMinutes) || SUDO_DEFAULT_MINUTES} min`;
  if (sudo.full) return `Full sudo, up to ${mins}`;
  const cmds = sudo.commands || [];
  if (!cmds.length) return "None";
  return `${cmds.length === 1 ? "1 command" : `${cmds.length} commands`}, up to ${mins}: ${cmds.join(", ")}`;
};

// A save the backend refused over ssh.sudo ("ssh.sudo: <reason>"), in plain
// words. null when the text isn't about sudo.
export const SUDO_ERROR_PREFIX = "ssh.sudo:";
export function sudoServerError(text) {
  const t = String(text || "").replace(/^Invalid policy:\s*/i, "").trim();
  if (!t.toLowerCase().startsWith(SUDO_ERROR_PREFIX)) return null;
  const r = t.slice(SUDO_ERROR_PREFIX.length).trim().toLowerCase();
  if (r.includes("absolute path")) {
    const m = /:\s*"([^"]+)"\s*$/.exec(t);
    return SUDO_WORDS.badCommand(m ? m[1] : "A command");
  }
  if (r.includes("at least one command")) return SUDO_WORDS.noCommands;
  if (r.includes("minutes")) return SUDO_WORDS.minutes;
  if (r.includes("at most")) return SUDO_WORDS.tooMany;
  return SUDO_WORDS.other;
}

/* --------------------------------------------------------------- accounts */

export const TEMP_ACCOUNT = "temp";
export const NO_KEY_ACCOUNTS = ["root", "authnull-svc", "nobody"];
export const isNoKeyAccount = (a) => NO_KEY_ACCOUNTS.includes(String(a || "").toLowerCase());
export const accountText = (a) => (a === TEMP_ACCOUNT ? "a temporary account" : a);

// The backend's account-name rule (sshProxyService accountNameError), in plain words.
export function accountProblem(name) {
  const n = String(name || "").trim();
  if (!n) return null;
  if (n.includes("@")) return `${n} can't contain @.`;
  if (n.length > 32) return `${n} is longer than 32 characters.`;
  if (n.startsWith("-")) return `${n} can't start with -.`;
  if (!/^[A-Za-z0-9._-]+$/.test(n)) return `${n} can only use letters, digits, . _ and -.`;
  return null;
}

/* ------------------------------------------------------------------ words */

const firstPlus = (list, fmt = (x) => x) => (list.length ? `${fmt(list[0])}${list.length > 1 ? ` +${list.length - 1}` : ""}` : "");

/** List "Where": "10.2.14.21 as ubuntu", "Any host as oracle +1". */
export function whereText(ssh) {
  const hosts = cleanList(ssh?.hosts);
  const accounts = cleanList(ssh?.accounts);
  return `${firstPlus(hosts) || "Any host"} as ${firstPlus(accounts, accountText) || "any account"}`;
}

// The engine's order inside a group (internal/sshpolicy specificity): one
// point each for hosts, accounts, a gateway, and named people.
export function sshSpecificity(policyJson) {
  const ssh = policyJson?.ssh || {};
  let n = 0;
  if (cleanList(ssh.hosts).length) n += 1;
  if (cleanList(ssh.accounts).length) n += 1;
  if ((ssh.jumpServers || []).length) n += 1;
  if (!ssh.matchAll && (cleanList(ssh.users).length || cleanList(ssh.groups).length)) n += 1;
  return n;
}

// The backend answers the nil UUID when nothing was created.
export const realId = (id) => (typeof id === "string" && id && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(id) ? id : null);
